import { PERMISSION_GROUPS } from '@repo/types'

/**
 * VRG-677 — a permission's name as an admin sees it in the role editor.
 *
 * `PERMISSION_GROUPS` (@repo/types) is the matrix's own source, so "ai_view" reads
 * "View AI" here exactly as it does in Settings → Roles & permissions. Before this the
 * only helper was `PermissionGate`'s slug humanizer, which turned `ai_view` into
 * "Ai view": a name no admin can find in the matrix, which defeats §27's point of
 * naming the permission at all.
 */

const LABEL_BY_NAME = new Map<string, string>()
const GROUP_BY_NAME = new Map<string, { index: number; label: string; read: boolean }>()

PERMISSION_GROUPS.forEach((group, index) => {
  for (const permission of group.permissions) {
    LABEL_BY_NAME.set(permission.name, permission.label)
    GROUP_BY_NAME.set(permission.name, { index, label: group.label, read: permission.operation === 'read' })
  }
})

// "manage_roles" → "Manage roles". Only for a name the catalog does not know.
const humanizeSlug = (name: string) => name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())

export const permissionLabel = (name: string): string => LABEL_BY_NAME.get(name) ?? humanizeSlug(name)

/**
 * The matrix GROUPS a set of permissions touches, in matrix order, plus whether every
 * permission the catalog knows is a read. Unknown names are ignored rather than guessed.
 */
export const permissionAreas = (names: readonly string[]): { areas: string[]; viewOnly: boolean } => {
  const groups = new Map<number, string>()
  let viewOnly = true
  for (const name of names) {
    const group = GROUP_BY_NAME.get(name)
    if (!group) continue
    groups.set(group.index, group.label)
    if (!group.read) viewOnly = false
  }
  const areas = [...groups.entries()].sort(([a], [b]) => a - b).map(([, label]) => label)
  return { areas, viewOnly }
}
